import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Input, List, Picker, Popup, Toast } from 'antd-mobile-v5'
import { FormattedMessage, useIntl } from 'react-intl'
import { isMainlandMobile, MAINLAND_ADDRESS_TYPE } from '../common/utils'
import { getEcards, getRedeemedFreeCards } from '../../reducers/selectors'
import { getWechatOpenId } from '../../utils/wechat'
import {
  useEcardGoods,
  useEcardOrderCreate,
  useEcardOrderPreview,
  useEcardPaymentStatus,
  useEcardCloseOrder,
  useEcardCoupons,
} from '../../services/hooks'
import { useGetChinaCities, useGetChinaProvinces } from '../FormTools/SelectRegion'
import { countFreeFirstCards, getPostageFromCatalog, hasPaidPhysicalItem } from '../../pages/BuyCards/cardOptionUtils'
import {
  createWposPayTypePicker,
  FUIOU_H5_PAY_TYPE,
  isValidFuiouUrl,
  isValidWposQrContent,
  pickFuiouChannel,
  safeExternalRedirect,
  safeFuiouRedirect,
} from '../FormTools/utils'
import FuiouQrPopup from '../FormTools/FuiouQrPopup'
import { isPreviewCheaper, runWithTimeout, sortCouponsByRate } from './ecardCouponUtils'
import { applyCouponDiscount, buildOrderItemList } from './ecardOrderItemUtils'
import EcardCouponPicker from './EcardCouponPicker'

import styles from './Cart.module.css'

export default function EcardCheckoutPopup({ visible, setVisible, items, valueAdded }) {
  const { formatMessage } = useIntl()
  // 免费首卡判定按条目 ecardId 查用户证书列表的 cardRequestType（与服务端归零口径一致）；
  // 已下单/已兑证书（redeemedFreeCards）不计免费
  const { ecards, redeemedFreeCards } = useSelector((state) => ({
    ecards: getEcards(state),
    redeemedFreeCards: getRedeemedFreeCards(state),
  }))
  // 收货地区固定中国大陆：购物车结算不做地区选择（对齐教材购买页大陆形态），
  // 省/市区为收货地址必选组成部分，下单时 addressType 恒为大陆枚举值
  const addressType = MAINLAND_ADDRESS_TYPE
  const [address, setAddress] = useState('')
  const [userName, setUserName] = useState('')
  const [userMobile, setUserMobile] = useState('')
  // 手机号格式校验的行内报错（显示在输入框下方），用户重新输入时清除
  const [mobileError, setMobileError] = useState('')
  // 收货表单各字段的行内报错（空值/未选择），改为输入框下方红字提示而非全局弹窗
  const [provinceError, setProvinceError] = useState('')
  const [cityError, setCityError] = useState('')
  const [addressError, setAddressError] = useState('')
  const [userNameError, setUserNameError] = useState('')
  // 大陆省市（addressType 为大陆时必选）：值传 PADI 参考接口清洗后的简体中文 label，
  // 与教材 ShippingAddress/BooksCheckout 同形态，后端按省市归一化匹配运费档
  const [provinceId, setProvinceId] = useState()
  const [province, setProvince] = useState()
  const [cityId, setCityId] = useState()
  const [city, setCity] = useState()
  const [qrVisible, setQrVisible] = useState(false)
  const [qrUrl, setQrUrl] = useState('')
  // 应付金额（CHNA-2618：createEcardOrder 响应新增 price/currency，反算自分值、与实扣一致）
  const [qrPrice, setQrPrice] = useState(null)
  const [qrCurrency, setQrCurrency] = useState(null)
  // 优惠券（本地实体卡85折券）：选中券 id（null = 不使用）；下单响应的 couponPrice 为服务端权威折扣金额
  const [selectedCouponId, setSelectedCouponId] = useState(null)
  const [qrDiscountPrice, setQrDiscountPrice] = useState(null)
  const [orderId, setOrderId] = useState(null)
  // WPOS 渠道选择结果（WECHAT/ALIPAY），传给 FuiouQrPopup 展示对应渠道边框色与文案
  const [qrPayType, setQrPayType] = useState(null)
  // 渠道选择面板等待期间禁用下单按钮（loading 此时恒为 false）
  const [pickingChannel, setPickingChannel] = useState(false)
  const wposPickerRef = useRef(null)
  // 互斥锁：防双击结算并发建单（AgentElearning 注释引用的即此处）
  const payingRef = useRef(false)
  // await picker 后组件可能已卸载：setState 前判 mounted 防卸载告警
  const mountedRef = useRef(true)

  // 商品目录加载态：itemNo 缺失时区分"目录未加载/加载失败"（竞态或网络问题，稍后重试）
  // 与"目录真未配置该卡面"（配置错误），两种场景给不同提示文案（见 checkout 内拦截）
  const { loading: catalogLoading, error: catalogError, data: goodsCatalog } = useEcardGoods()
  const catalogPending = catalogLoading || !!catalogError

  // 可用优惠券列表：辅助能力，加载失败静默（onError 返回 true 抑制全局错误弹窗）——
  // 券接口暂不可用/未部署不应阻塞结算主流程。
  // loading：非实体卡订单（无收货表单）在券加载完成前不直接下单，避免漏过刚加载出来的可用券
  const { data: coupons, loading: couponsLoading } = useEcardCoupons({ onError: () => true })

  // 订单明细 itemNo 全集（证书卡 + 增值商品），用于券可用性判定
  const orderItemNos = useMemo(
    () => [...(items || []), ...((valueAdded || []).map((v) => ({ itemNo: v.type })) || [])].map((item) => item.itemNo),
    [items, valueAdded]
  )

  // 订单明细（试算与下单同构）：items 完整透传 + 增值权益转 { itemNo, quantity }
  const checkoutItems = useMemo(
    () => [...(items || []), ...((valueAdded || []).map((v) => ({ itemNo: v.type, quantity: 1 })) || [])],
    [items, valueAdded]
  )

  // 免费首卡数量（用于本地运费预估：有免费首卡行 → 目录运费一次）。
  // 2026-09-17 起服务端取消「每单限 1 张」：一单可含多张免费卡（多张教练证书各兑一张，
  // 同证书重复明细按目录价 99 计），前端不再做 >1 拦截。资格按条目 ecardId 查用户证书
  // 列表的 cardRequestType（与服务端归零口径一致；购物车条目无 skuId 不再走 cardOptions）
  const freeFirstCount = useMemo(
    () => countFreeFirstCards(checkoutItems, ecards, redeemedFreeCards),
    [checkoutItems, ecards, redeemedFreeCards]
  )

  // 订单内容签名：弹窗关闭组件不卸载，券选中态会残留到下一次打开。
  // 订单内容变化（实体卡↔电子卡切换、增删商品）视为新订单，见下方重置 effect。
  // rich 条目（BuyCards 本地，skuId 存在）保持原身份——itemNo 是本地目录解析结果，
  // 加载完成时会从 null 变为 SKU，若入签名会误判"订单变化"清空用户已选的券；
  // slim 条目（服务端购物车）itemNo 恒在，用 ecardId+itemNo+type 签名
  const orderKey = useMemo(
    () =>
      (checkoutItems || [])
        .map((i) => {
          // 增值权益（无 skuId/ecardId）用 itemNo（type 恒稳定）
          if (i.skuId != null) return `${i.ecardId ?? ''}-${i.skuId}-${i.typeName ?? ''}`
          if (i.ecardId != null) return `${i.ecardId}-${i.itemNo ?? ''}-${i.type ?? ''}`
          return `${i.itemNo ?? ''}-${i.quantity ?? 1}`
        })
        .join('|'),
    [checkoutItems]
  )

  // 确认订单商品明细（对齐淘宝订单确认页形态）：卡面 + 增值权益逐条展示名称/规格/单价。
  // 卡面单价取 ecard_goods 目录价（元，与试算/下单定价同源），不用条目自带 price
  //（购物车条目是分、有赞 SKU 是元，两入口单位不一致）；缩略图取条目自带图（picUrl/imageUrl）
  const orderItemList = useMemo(
    () => buildOrderItemList(items, valueAdded, goodsCatalog),
    [items, valueAdded, goodsCatalog]
  )

  // 实体卡判定（STANDARD_PHYSICAL，MEMBER_CARD 已退役）：决定收货表单是否渲染；
  // 优惠券区对所有订单开放（非实体卡订单也可用券），权威校验在服务端
  // （getAuthoritative + requires.physical / requires.single.physical）
  const physicalItemNos = useMemo(
    () =>
      new Set((goodsCatalog || []).filter((goods) => goods.type === 'STANDARD_PHYSICAL').map((goods) => goods.itemNo)),
    [goodsCatalog]
  )
  const hasPhysicalCard = useMemo(
    () => orderItemNos.some((itemNo) => itemNo && physicalItemNos.has(itemNo)),
    [orderItemNos, physicalItemNos]
  )

  // 运费（元）：取目录 EXPRESS_FEE 运费商品价（后端维护）；运费口径（2026-09-16 起、
  // 2026-09-17 收紧）：普通实体卡/券单包邮，只有整单没有付费实体卡的免费首卡单收取；
  // 纯电子卡/权益 0。付费实体卡同车即包邮（其价已含运费、一个快递寄出）
  const hasPaidPhysical = useMemo(
    () => hasPaidPhysicalItem(checkoutItems, ecards, redeemedFreeCards),
    [checkoutItems, ecards, redeemedFreeCards]
  )
  const postageAmount = useMemo(
    () => getPostageFromCatalog(goodsCatalog, orderItemNos, freeFirstCount, hasPaidPhysical),
    [goodsCatalog, orderItemNos, freeFirstCount, hasPaidPhysical]
  )

  // 确认订单金额（试算返回前的回退口径）：卡面商品取 ecard_goods 目录价（元，与试算/下单同源）
  // + 增值权益（元）+ 运费（目录价）。服务端权威金额（含优惠券折扣）以下单响应 price 为准
  const totalAmount = useMemo(() => {
    const cardsYuan = (items || []).reduce((sum, item) => {
      const goods = (goodsCatalog || []).find((g) => g.itemNo === item.itemNo)
      return sum + (goods ? Number(goods.price) || 0 : 0)
    }, 0)
    const addedYuan = (valueAdded || []).reduce((sum, v) => sum + (Number(v.price) || 0), 0)
    return cardsYuan + addedYuan + postageAmount
  }, [items, valueAdded, goodsCatalog, postageAmount])

  // 大陆地址省市选择器：数据源复用教材组件 SelectRegion 的 PADI 参考接口 hooks
  // （选项 label 已清洗为简体中文），市区依赖所选省才有数据
  const { data: provinceOptions } = useGetChinaProvinces()
  const { data: cityOptions } = useGetChinaCities(provinceId)

  // 换省清空市区（市区列表随省刷新，残留旧市值会与所选省不匹配）
  const onProvinceConfirm = useCallback(
    (vals) => {
      const stateId = vals[0]
      const selected = (provinceOptions || []).find((p) => String(p.value) === String(stateId))
      setProvinceId(stateId)
      setProvince(selected ? selected.label : undefined)
      setCityId(undefined)
      setCity(undefined)
      setProvinceError('')
      setCityError('')
    },
    [provinceOptions]
  )

  const onCityConfirm = useCallback(
    (vals) => {
      const areaId = vals[0]
      const selected = (cityOptions || []).find((c) => String(c.value) === String(areaId))
      setCityId(areaId)
      setCity(selected ? selected.label : undefined)
      setCityError('')
    },
    [cityOptions]
  )

  const { loading, run: submitOrder } = useEcardOrderCreate({
    onSuccess: (data) => {
      const { url, orderId: oid, price, currency, couponPrice, paid } = data
      // 服务端权威折扣金额（无券为 null）：透传 QR 弹窗展示「已优惠」行
      setQrDiscountPrice(couponPrice ?? null)
      const channel = pickFuiouChannel()
      if (paid === true) {
        // 0 元免支付单（券全额抵扣 + 包邮，运费口径改造 2026-09-16）：后端已直接履约
        // 置 PAID、无支付串（url 为 null），与扫码支付成功同为终态。必须先判 paid 再判 url；
        // 免支付没有二维码弹窗：直接关结算弹窗并复用成功提示（afterClose 里 reload 清购物车）
        setVisible(false)
        handlePaymentSuccess()
      } else if (channel === 'WPOS' && isValidWposQrContent(url)) {
        // PC WPOS 扫码分支：单码通道返回渠道二维码内容（qr.alipay.com / weixin://），
        // 只做本地编码展示，校验用二维码内容白名单（兼容历史富友聚合链接形态）。
        // 先验白名单再进分支：WPOS 下后端若返回海外通道（Stripe/TradeVan）整页链接，
        // 不属于二维码内容，落到下方 safeExternalRedirect 整页跳转，不被白名单误拦截
        if (!oid) {
          // orderId 缺失无法轮询，直接提示失败，避免二维码永挂无反馈
          Toast.show({ icon: 'fail', content: formatMessage({ id: 'fuiou-pay-fail' }) })
          return
        }
        setQrUrl(url)
        setQrPrice(price)
        setQrCurrency(currency)
        setOrderId(oid)
        // 下单成功进入扫码：关闭收货信息表单弹窗，避免表单与二维码弹窗叠加
        // （表单字段状态保留在组件内，关闭二维码后可重新点「去结算」带回原值）
        setVisible(false)
        setQrVisible(true)
      } else if (isValidFuiouUrl(url)) {
        safeFuiouRedirect(url, formatMessage({ id: 'fuiou-pay-url-invalid' }))
      } else if (url) {
        // 海外 Stripe/TradeVan 等通道：非富友域名，跳转前校验 http(s) 协议
        safeExternalRedirect(url, formatMessage({ id: 'fuiou-pay-url-invalid' }), {
          // 白名单外域名弹确认（message 由 safeExternalRedirect 以 hostname 调用）
          message: (host) => formatMessage({ id: 'fuiou-external-redirect-confirm' }, { host }),
          confirm: formatMessage({ id: 'common-comfirmBtn' }),
          cancel: formatMessage({ id: 'common-cancel' }),
        })
      } else {
        // 下单成功但无 url 且非免支付单（异常形态）：提示失败，避免弹窗静默无反馈
        Toast.show({ icon: 'fail', content: formatMessage({ id: 'fuiou-pay-fail' }) })
      }
    },
    onError: (error) => {
      // CHNA-2618：服务端校验纯权益单返回 ecard.rights.requires.card，走 Toast 提示而非全局 Modal
      if (error && error.code === 'ecard.rights.requires.card') {
        Toast.show({ icon: 'fail', content: formatMessage({ id: 'ecard-rights-requires-card' }) })
        return true
      }
      // 未识别的错误码返回 false，走全局错误 Modal
      return false
    },
  })

  // 轮询失败时抑制 useRequestCommon 的全局错误 Modal（轮询类请求不弹窗）
  const { runAsync: checkPaymentStatus } = useEcardPaymentStatus({ manual: true, onError: () => true })
  // 关单只由用户显式「取消订单」触发（弹窗关闭/超时都不再关单，见 FuiouQrPopup）：
  // 失败向上抛，由弹窗静默收起——后端幂等，失败时订单仍是 WAIT_PAY，可再次发起支付
  const { runAsync: closeEcardOrder } = useEcardCloseOrder({ manual: true, onError: () => true })
  const cancelOrder = useCallback(async () => {
    if (!orderId) return
    await closeEcardOrder(orderId)
  }, [closeEcardOrder, orderId])

  const checkStatus = useCallback(async () => {
    const result = await checkPaymentStatus(orderId)
    // hooks 层返回裸 body。后端确认（2026-08-16）：paymentMethod 建单时即写入，
    // 端点仅在 status=PAID/TRADE_PAID 时返回非 null，两者判定等价；
    // 统一按显式终态 status 判定（对齐 BooksCheckout/CoursePicOrder），两个终态都判成功：
    // 漏判会把已付款订单误判为未支付，诱导用户重复下单付款（二次扣款）
    return !!(result && ['PAID', 'TRADE_PAID'].includes(result.status))
  }, [checkPaymentStatus, orderId])

  const handlePaymentSuccess = useCallback(() => {
    setQrVisible(false)
    Toast.show({
      icon: 'success',
      content: formatMessage({ id: 'fuiou-pay-success' }),
      afterClose: () => window.location.reload(),
    })
  }, [formatMessage])

  // 组件卸载时清理残留的命令式渠道选择面板
  useEffect(() => {
    // StrictMode 下 effect 会 setup→cleanup→setup 重跑：cleanup 置 false 后必须复位，
    // 否则重挂载后 mountedRef 恒 false，支付回调全部失效
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (wposPickerRef.current) {
        wposPickerRef.current.close()
        wposPickerRef.current = null
      }
    }
  }, [])

  // 行内报错在弹窗表单上部、提交按钮在下部：任一字段报错后滚动到第一个报错位置
  //（class 名在产物中会被 hash，用属性子串匹配；报错 DOM 为 state 更新后渲染，等下一帧再查）
  useEffect(() => {
    const errorKey = [provinceError, cityError, addressError, userNameError, mobileError].join('|')
    if (!errorKey) return undefined
    const timer = setTimeout(() => {
      const popup = document.querySelector('[class*="checkout-popup"]')
      const firstError = popup ? popup.querySelector('[class*="field-error"]') : null
      if (firstError) firstError.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }, 100)
    return () => clearTimeout(timer)
  }, [provinceError, cityError, addressError, userNameError, mobileError])

  // 非实体卡订单（纯电子卡/增值权益）无需填写收货信息：
  // 无可用券时直接下单不弹窗（避免二次确认）；有可用券时必须先弹窗选券。
  // 券加载中不判定（避免把加载中/失败误判为无券直接下单）。目录加载完成前
  // 无法判定是否含实体卡，等待加载完成再判定
  const hasCoupons = useMemo(() => !!coupons && coupons.length > 0, [coupons])
  const skipPopup = !catalogPending && !hasPhysicalCard && !couponsLoading && !hasCoupons

  useEffect(() => {
    if (visible && skipPopup) {
      setVisible(false)
      checkout()
    }
    // checkout 每次渲染都是新引用，故意不入依赖：只在弹窗打开/目录/实体/券判定变化时检查
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, skipPopup])

  // previewData 对应的订单/券口径：仅在试算成功后更新（requestRef 记录请求时口径，
  // onSuccess 时写入 infoRef），用于判断数据是否属于当前订单（换单后清空、
  // 探测结果复用避免重复请求、同口径请求去重）
  const previewRequestRef = useRef(null)
  const previewInfoRef = useRef(null)

  // 订单金额试算：计价与下单完全同源（含优惠券预估折扣），返回字符串金额
  // { itemsPrice, expressPrice, couponPrice|null, totalPrice }，只展示不计算
  const {
    data: previewData,
    run: runPreview,
    mutate: mutatePreview,
  } = useEcardOrderPreview({
    onSuccess: () => {
      previewInfoRef.current = previewRequestRef.current
    },
    // CHNA-2618：纯权益单试算时服务端返回 ecard.rights.requires.card，走 Toast 而非全局 Modal
    onError: (error) => {
      if (error && error.code === 'ecard.rights.requires.card') {
        Toast.show({ icon: 'fail', content: formatMessage({ id: 'ecard-rights-requires-card' }) })
        return true
      }
      return false
    },
  })
  // 自动选券探测用试算实例：与展示实例分离，探测失败静默（onError 返回 true 抑制全局弹窗）
  const { runAsync: previewProbe } = useEcardOrderPreview({ manual: true, onError: () => true })

  // 选中券时给实体卡条目分摊折后价：以试算权威折扣总额 couponPrice 按目录价占比分摊，
  // 各行折后价之和与页脚（商品金额 − 优惠）一致；电子卡/增值权益不参与、无折后价
  const orderItemRows = useMemo(() => {
    if (selectedCouponId == null || previewData == null || previewData.couponPrice == null) return orderItemList
    return applyCouponDiscount(orderItemList, physicalItemNos, previewData.couponPrice)
  }, [orderItemList, selectedCouponId, previewData, physicalItemNos])

  // 试算触发：明细就绪（itemNo 缺失下单前会拦截，跳过避免坏明细报错）且弹窗打开/换券时请求。
  // 省市不参与 eCard 运费计算（固定价），试算请求不携带
  const previewReady = !catalogPending && checkoutItems.length > 0 && checkoutItems.every((i) => i.itemNo)

  useEffect(() => {
    if (!visible || !previewReady) return
    const request = { orderKey, couponId: selectedCouponId ?? undefined }
    const info = previewInfoRef.current
    // 已有同口径权威数据（含探测 mutate 写入的探测结果）时不重复请求
    if (info && info.orderKey === request.orderKey && info.couponId === request.couponId) return
    // 订单变化瞬间 selectedCouponId 可能还是上一订单的券：跳过本轮，等重置 effect
    // 清券后以新订单口径重试（带旧券试算新订单会被后端拒绝并弹全局错误框）
    if (request.couponId != null) {
      const lastRequest = previewRequestRef.current
      if (lastRequest && lastRequest.orderKey !== orderKey) return
    }
    previewRequestRef.current = request
    runPreview({ items: checkoutItems, couponId: request.couponId })
    // runPreview 每次渲染都是新引用，故意不入依赖：只在弹窗打开/换单/换券时请求
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, previewReady, selectedCouponId, orderKey])

  // 自动带入最佳优惠券：弹窗打开、用户未手动选过券时，按折扣率升序（0元券最前，优惠单调递增）
  // 串行试算探测，第一张试算通过且比无券基线更便宜的券自动选中。试算与下单同源
  // （后端契约：试算通过则下单金额层面必通过），排序顺序即最优顺序。
  // 用户手动选券（含「不使用优惠券」）后不再自动；探测全失败保持不选且不重试
  const userTouchedCouponRef = useRef(false)
  const autoProbeDoneRef = useRef(false)
  // 探测期间禁用提交按钮：避免用户在最优券试算完成前按无券金额提交，错失优惠
  const [probePending, setProbePending] = useState(false)
  // 探测轮次序号：旧一轮探测的收尾（finally）不得误清新一轮的按钮锁定（probePending），
  // 见探测 effect 内 seq 比对（竞态修复）
  const probeSeqRef = useRef(0)

  const onCouponSelect = useCallback((id) => {
    userTouchedCouponRef.current = true
    setSelectedCouponId(id)
  }, [])

  // 订单内容变化时清空已选券并重置选券状态：券可用性由订单内容决定（实体卡限定券等，
  // 权威校验在服务端），沿用上一订单选的券会试算出错（实体卡券用于电子卡订单报
  // 「该优惠券仅限实体卡订单使用」）；重置 refs 让新订单重新自动探测最优券。
  // 同时清空试算数据（previewData 是上一订单的金额，清空避免金额区闪现旧值，
  // 展示 effect 会按新订单重新请求）。首次挂载跑一次是空操作，无副作用
  useEffect(() => {
    setSelectedCouponId(null)
    userTouchedCouponRef.current = false
    autoProbeDoneRef.current = false
    mutatePreview(undefined)
    previewInfoRef.current = null
    previewRequestRef.current = null
    // mutatePreview 每次渲染都是新引用，故意不入依赖：只在订单内容变化时重置
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderKey])

  // 商品金额为 0（club 兑换单等，试算 itemsPrice 为 "0.00"）：无可优惠金额，
  // 券选择框置灰展示说明且不可点击；已选中的券同步清空，下单不携带 couponId
  const couponUnavailable = !!previewData && Number(previewData.itemsPrice) <= 0

  useEffect(() => {
    if (couponUnavailable && selectedCouponId != null) setSelectedCouponId(null)
  }, [couponUnavailable, selectedCouponId])

  useEffect(() => {
    if (!visible || !previewReady || !coupons || coupons.length === 0) return
    if (userTouchedCouponRef.current || selectedCouponId != null || autoProbeDoneRef.current) return
    let cancelled = false
    const seq = ++probeSeqRef.current
    const probe = async () => {
      setProbePending(true)
      // 完整跑完（选中券/全部未选中/金额为0无需券）才置 true；基线失败/超时不消耗标记，
      // 下次打开弹窗重新探测（否则一次网络抖动永久错过该订单的自动选券）
      let finished = false
      try {
        // 无券基线：只选试算更便宜的券，防 rate 异常数据选出更贵的结果；
        // 带超时（runWithTimeout）：请求挂起时按钮不被永久锁死，超时按失败处理
        const base = await runWithTimeout(previewProbe({ items: checkoutItems }))
        if (cancelled) return
        // 商品金额为 0（club 兑换单等）：无可优惠金额，券无意义，跳过带券探测
        if (base && Number(base.itemsPrice) <= 0) {
          finished = true
          return
        }
        const baseTotal = base ? base.totalPrice : null
        for (const coupon of sortCouponsByRate(coupons)) {
          try {
            const result = await runWithTimeout(previewProbe({ items: checkoutItems, couponId: coupon.id }))
            if (cancelled) return
            if (isPreviewCheaper(result, baseTotal) && mountedRef.current) {
              setSelectedCouponId(coupon.id)
              // 展示试算复用探测结果（与展示请求同口径），选中后不再重复请求同一试算
              mutatePreview(result)
              previewInfoRef.current = { orderKey, couponId: coupon.id }
              finished = true
              return
            }
          } catch (error) {
            // 该券试算失败（后端拒绝：券不适用当前订单等，属预期）或超时：跳过继续探测
            // 下一张，不中断整个循环，否则排在其后的可用券全部漏测。留 warn 日志——
            // 系统性失败（如券接口全挂）时生产环境有信号可查
            console.warn('[ecard-probe] 券试算失败/超时，跳过继续探测：', coupon.id, error)
          }
        }
        finished = true
      } catch (error) {
        // 基线失败/超时：保持不选券、不标记完成（finished 仍为 false），下次打开弹窗
        // 重新探测；用户仍可手动选券（选中后的展示试算给出权威结果）。留 error 日志，
        // 系统性基线失败（网络/后端问题）生产环境有信号可查
        console.error('[ecard-probe] 基线试算失败/超时，跳过自动选券：', error)
      } finally {
        // 只允许最新一轮探测解锁按钮：被取消且无新轮次（关弹窗/手动选券）时 seq 未变、清锁；
        // 已有更新轮次时 seq 已增，旧轮不得误清新一轮的锁定（竞态修复）
        if (mountedRef.current && probeSeqRef.current === seq) setProbePending(false)
      }
      // 探测完整跑完才标记完成；被取消（弹窗关闭/用户手动选券）或基线失败时
      // 不消耗标记，下次打开弹窗重新探测
      if (!cancelled && finished) autoProbeDoneRef.current = true
    }
    probe()
    return () => {
      cancelled = true
    }
    // checkoutItems/previewProbe 每次渲染都是新引用，故意不入依赖：只在弹窗打开/明细就绪/
    // 券列表就绪/订单内容变化（orderKey）时探测一轮。orderKey 入依赖：订单变化时
    // 旧探测闭包里的 checkoutItems 已过期，必须取消重跑，否则可能按旧订单选出券
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, previewReady, coupons, selectedCouponId, orderKey])

  const checkout = async () => {
    if (payingRef.current) return
    payingRef.current = true
    try {
      // CHNA-2618：增值服务（实体卡85折/周边8折）不能单独购买，必须搭配至少一个已选卡面的卡
      if (valueAdded && valueAdded.length > 0 && (!items || items.length === 0)) {
        Toast.show({ icon: 'fail', content: formatMessage({ id: 'ecard-rights-requires-card' }) })
        return
      }
      // 只有包含运费的实体卡订单才需要收货人信息：电子卡/纯权益订单无配送，跳过收货表单校验
      if (hasPhysicalCard) {
        // 校验顺序与表单从上到下一致：省 → 市 → 地址 → 联系人 → 手机号
        // 省市必选（对齐教材契约：运费档由后端按省市归一化匹配，缺失无法计价）
        if (!province) {
          setProvinceError(formatMessage({ id: 'addressForm-provinceHint' }))
          return
        }
        if (!city) {
          setCityError(formatMessage({ id: 'addressForm-cityHint' }))
          return
        }
        if (!address) {
          setAddressError(formatMessage({ id: 'addressForm-saveHint' }))
          return
        }
        if (!userName) {
          setUserNameError(formatMessage({ id: 'addressForm-saveHint' }))
          return
        }
        if (!userMobile) {
          setMobileError(formatMessage({ id: 'addressForm-saveHint' }))
          return
        }
        // 收货地区恒为大陆：手机号必须是 11 位大陆手机号（后端已限制，前端先行拦截），错误直接显示在输入框下方
        if (!isMainlandMobile(userMobile.trim())) {
          setMobileError(formatMessage({ id: 'addressForm-mobileHint' }))
          return
        }
      }

      const channel = pickFuiouChannel()
      let payType = channel === 'H5PAY' ? FUIOU_H5_PAY_TYPE.WECHAT_JSAPI : undefined
      // 后端要求 WPOS 显式传 payType（FAPPLET 聚合码已下线），下单前先弹渠道选择
      if (channel === 'WPOS') {
        const picker = createWposPayTypePicker(formatMessage)
        wposPickerRef.current = picker
        setPickingChannel(true)
        try {
          payType = await picker.promise
        } finally {
          if (mountedRef.current) setPickingChannel(false)
          wposPickerRef.current = null
        }
        // 用户取消渠道选择，静默返回
        if (!payType) return
        if (mountedRef.current) setQrPayType(payType)
      }
      // createEcardOrder 的 itemNo 必填（查 ecard_goods 唯一键）：目录未配置卡面/历史购物车
      // 旧数据匹配失败时拦截并提示，避免下单 400 后用户无感知。
      // 目录未加载完成（弱网点击过快）或加载失败时 itemNo 缺失是竞态/网络问题而非配置缺失，
      // 提示"稍后重试"而非"商品未配置"，避免误导用户
      if (checkoutItems.some((i) => !i.itemNo)) {
        Toast.show({
          icon: 'fail',
          content: formatMessage({ id: catalogPending ? 'fuiou-goods-loading' : 'fuiou-goods-unconfigured' }),
        })
        return
      }
      const checkoutData = {
        // 收货信息仅实体卡订单携带：电子卡/纯权益订单无配送不传（undefined 由 JSON 序列化剔除）
        ...(hasPhysicalCard
          ? {
              addressType,
              address,
              userName,
              userMobile,
              // 省市传 PADI 清洗后的简体中文 label，后端按省市归一化匹配运费档
              province,
              city,
            }
          : {}),
        items: checkoutItems,
        // 选中券才传 couponId（undefined 被 JSON.stringify 丢弃，与后端可选字段对齐）
        couponId: selectedCouponId ?? undefined,
        channel,
        payType,
        openid: getWechatOpenId(),
      }

      submitOrder(checkoutData)
    } finally {
      payingRef.current = false
    }
  }

  const onClose = () => {
    setVisible(false)
  }

  return (
    <>
      <Popup
        visible={visible && !skipPopup}
        onMaskClick={onClose}
        bodyStyle={{
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          minHeight: '60vh',
          // 商品过多时弹窗内容超高：限制弹窗最高 85vh，中间内容区滚动、支付按钮固定底部
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div className={styles['checkout-popup']}>
          <div className={styles['popup-header']}>
            <span className={styles['popup-title']}>
              <FormattedMessage id={hasPhysicalCard ? 'addressForm-add' : 'cart-checkout-title'} />
            </span>
            <span
              className={styles['popup-close']}
              role="button"
              tabIndex={0}
              aria-label={formatMessage({ id: 'common-close' })}
              onClick={onClose}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  onClose()
                }
              }}
            >
              ✕
            </span>
          </div>

          {/* 中间滚动区：商品明细/收货表单/优惠券/金额区，商品过多超高时内部滚动 */}
          <div className={styles['checkout-scroll']}>
            {/* 订单商品明细：弹窗顶部逐条展示（缩略图/名称/规格/单价），数量恒为 1 不展示 ×N；
              选中券后实体卡条目展示折后价（原价划线 + 券色折后价，与页脚权威金额一致） */}
            {orderItemRows.length > 0 && (
              <div className={styles['checkout-goods']}>
                {orderItemRows.map((row) => (
                  <div className={styles['checkout-goods-item']} key={row.key}>
                    {row.imageUrl && (
                      <img className={styles['checkout-goods-img']} src={row.imageUrl} alt="" loading="lazy" />
                    )}
                    <div className={styles['checkout-goods-info']}>
                      <div className={styles['checkout-goods-name']}>
                        {/* 增值权益已知标题走 nameId（i18n），未知标题回退原始 name */}
                        {row.nameId ? formatMessage({ id: row.nameId }) : row.name}
                      </div>
                      {row.detail && <div className={styles['checkout-goods-detail']}>{row.detail}</div>}
                    </div>
                    {/* 目录未匹配（未加载/卡面未配置）时展示占位，避免误导 */}
                    <span className={styles['checkout-goods-price']}>
                      {row.price == null ? (
                        formatMessage({ id: 'ecard-price-placeholder' })
                      ) : row.discountedPrice != null ? (
                        <>
                          <span className={styles['checkout-goods-original']}>￥{row.price.toFixed(2)}</span>
                          <span className={styles['checkout-goods-discounted']}>
                            ￥{row.discountedPrice.toFixed(2)}
                          </span>
                        </>
                      ) : (
                        <span>￥{row.price.toFixed(2)}</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {hasPhysicalCard && (
              <List>
                <Picker
                  title={formatMessage({ id: 'setMobile-chooseRegion' })}
                  columns={[provinceOptions || []]}
                  value={provinceId ? [provinceId] : []}
                  onConfirm={onProvinceConfirm}
                >
                  {(items, actions) => (
                    // render-children 模式下 Picker 不注入点击事件，必须手动接 actions.open，
                    // 否则点击行无反应、选择面板弹不出来（2026-08-17 修复）
                    <List.Item
                      className={styles['picker-item']}
                      prefix={<FormattedMessage id="addressForm-province" />}
                      onClick={actions.open}
                    >
                      {items.length ? (
                        items[0].label
                      ) : (
                        <span className={styles['picker-placeholder']}>{formatMessage({ id: 'common-select' })}</span>
                      )}
                    </List.Item>
                  )}
                </Picker>
                {provinceError && <div className={styles['field-error']}>{provinceError}</div>}
                {/* 市区选择器常驻展示，未选省时禁用（cities 接口依赖省）；所有省市一律必选市区，
                与教材 ShippingAddress 一致 */}
                <Picker
                  title={formatMessage({ id: 'setMobile-chooseRegion' })}
                  columns={[cityOptions || []]}
                  value={cityId ? [cityId] : []}
                  onConfirm={onCityConfirm}
                >
                  {(items, actions) => (
                    <List.Item
                      className={styles['picker-item']}
                      prefix={<FormattedMessage id="addressForm-city" />}
                      disabled={!province}
                      onClick={actions.open}
                    >
                      {items.length ? (
                        items[0].label
                      ) : (
                        <span className={styles['picker-placeholder']}>{formatMessage({ id: 'common-select' })}</span>
                      )}
                    </List.Item>
                  )}
                </Picker>
                {cityError && <div className={styles['field-error']}>{cityError}</div>}
                <List.Item prefix={<FormattedMessage id="addressForm-address" />}>
                  <Input
                    placeholder={formatMessage({ id: 'addressForm-addressDetail' })}
                    value={address}
                    onChange={(value) => {
                      if (addressError) setAddressError('')
                      setAddress(value)
                    }}
                    maxLength={100}
                  />
                </List.Item>
                {addressError && <div className={styles['field-error']}>{addressError}</div>}
                <List.Item prefix={<FormattedMessage id="addressForm-contact" />}>
                  <Input
                    placeholder={formatMessage({ id: 'addressForm-defaultName' })}
                    value={userName}
                    maxLength={50}
                    onChange={(value) => {
                      if (userNameError) setUserNameError('')
                      setUserName(value)
                    }}
                  />
                </List.Item>
                {userNameError && <div className={styles['field-error']}>{userNameError}</div>}
                <List.Item prefix={<FormattedMessage id="addressForm-mobile" />}>
                  <Input
                    placeholder={formatMessage({ id: 'setMobile-mobileInput' })}
                    value={userMobile}
                    maxLength={11}
                    type="tel"
                    onChange={(value) => {
                      if (mobileError) setMobileError('')
                      setUserMobile(value.replace(/\D/g, '').slice(0, 11))
                    }}
                  />
                  {mobileError && <div className={styles['field-error']}>{mobileError}</div>}
                </List.Item>
              </List>
            )}

            {/* 优惠券区对所有订单开放（无券时组件自渲染 null）；
              parentVisible：外层结算弹窗关闭时联动收起选券二级弹层；
              商品金额为 0 时 unavailable：整行置灰展示说明、不可点击 */}
            <EcardCouponPicker
              coupons={coupons}
              parentVisible={visible}
              selectedId={selectedCouponId}
              onSelect={onCouponSelect}
              unavailable={couponUnavailable}
              unavailableText={<FormattedMessage id="ecard-coupon-zero-amount" />}
            />

            <div className={styles['popup-footer']}>
              {/* 金额区：试算接口（与下单同源计价）返回后展示 商品金额/运费/优惠/实付，选中券后随试算刷新；
                试算未返回前回退本地口径（目录运费 + 商品小计），无优惠行 */}
              {previewData ? (
                <>
                  <div className={styles['popup-amount']}>
                    <FormattedMessage id="ecard-preview-items" />
                    <span className={styles['popup-amount-price']}>￥{previewData.itemsPrice}</span>
                  </div>
                  <div className={styles['popup-postage']}>
                    <FormattedMessage id="cardRequest-postage" />
                    <span className={styles['popup-postage-price']}>￥{previewData.expressPrice}</span>
                  </div>
                  {previewData.couponPrice != null && (
                    <div className={styles['popup-discount']}>
                      <FormattedMessage
                        id="ecard-coupon-discounted"
                        values={{ amount: `￥${previewData.couponPrice}` }}
                      />
                    </div>
                  )}
                  <div className={styles['popup-total']}>
                    <FormattedMessage id="cardRequest-total" />
                    <span className={styles['popup-total-price']}>￥{previewData.totalPrice}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className={styles['popup-postage']}>
                    <FormattedMessage id="cardRequest-postage" />
                    <span className={styles['popup-postage-price']}>￥{postageAmount.toFixed(2)}</span>
                  </div>
                  <div className={styles['popup-total']}>
                    <FormattedMessage id="cardRequest-total" />
                    <span className={styles['popup-total-price']}>
                      {/* 含免费首卡的订单回退口径会按目录价 99 累加（错误展示 ￥119），
                      试算返回前用占位避免 119→20 跳变误导 */}
                      {freeFirstCount >= 1
                        ? formatMessage({ id: 'ecard-price-placeholder' })
                        : `￥${totalAmount.toFixed(2)}`}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* 支付按钮固定底部：商品过多时中间内容区滚动，按钮始终可见可点 */}
          <div className={styles['checkout-actions']}>
            {/* 目录加载完成前禁用：实体卡判定依赖目录，未判定完成就提交会漏收货信息校验；
                非实体卡订单券加载完成前禁用：避免券刚加载出来订单已下完，用户漏用券；
                自动选券探测期间禁用：避免用户按无券金额提交，错失自动带入的优惠 */}
            <Button
              color="primary"
              block
              onClick={checkout}
              loading={loading}
              disabled={
                loading || pickingChannel || catalogPending || (!hasPhysicalCard && couponsLoading) || probePending
              }
            >
              <FormattedMessage id="checkout" />
            </Button>
          </div>
        </div>
      </Popup>
      <FuiouQrPopup
        visible={qrVisible}
        url={qrUrl}
        price={qrPrice}
        currency={qrCurrency}
        discountPrice={qrDiscountPrice}
        onClose={() => setQrVisible(false)}
        onPaymentSuccess={handlePaymentSuccess}
        checkStatus={checkStatus}
        payType={qrPayType}
        onCancelOrder={cancelOrder}
      />
    </>
  )
}
